import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";

/**
 * Phase 7.1 Milestone C — 🏆 Top Gifters platform-wide leaderboard.
 *
 * Three tabs (Weekly / Monthly / Lifetime) over /api/top-gifters/leaderboard.
 * Mobile-first row layout. Supporter badges (🐐 / 💎) show next to each
 * row; rare-rarest first.
 */
const TABS = [
  { key: "weekly",   label: "Weekly",   sub: "Last 7 days" },
  { key: "monthly",  label: "Monthly",  sub: "Last 30 days" },
  { key: "lifetime", label: "Lifetime", sub: "All time" },
];

const RankMedal = ({ rank }) => {
  if (rank === 1) return <span className="text-3xl">🥇</span>;
  if (rank === 2) return <span className="text-3xl">🥈</span>;
  if (rank === 3) return <span className="text-3xl">🥉</span>;
  return (
    <span className="text-pink-200/70 text-sm font-bold w-8 text-center">
      #{rank}
    </span>
  );
};

const BadgeChip = ({ badge }) => (
  <span
    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10px] font-semibold text-white/90"
    title={badge.label}
  >
    <span>{badge.emoji}</span>
    <span className="hidden sm:inline">{badge.label}</span>
  </span>
);

const TopGiftersPage = () => {
  const [period, setPeriod] = useState("weekly");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/top-gifters/leaderboard?period=${period}&limit=25`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ leaderboard: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  const list = data?.leaderboard || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0514] via-[#0a0a14] to-black text-white">
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-10" data-testid="top-gifters-page">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">
              🏆 Top Gifters
            </h1>
            <p className="text-pink-200/70 text-xs sm:text-sm mt-1">
              The fans pushing real culture forward.
            </p>
          </div>
          <Link
            to="/wallet"
            className="text-xs px-3 py-1.5 rounded-full bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-200 font-semibold"
            data-testid="top-gifters-wallet-link"
          >
            🪙 Top up
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-5 border-b border-white/10" data-testid="top-gifters-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setPeriod(t.key)}
              className={`px-4 py-2.5 -mb-px border-b-2 transition-all text-sm font-semibold ${
                period === t.key
                  ? "border-amber-400 text-white"
                  : "border-transparent text-pink-200/70 hover:text-white"
              }`}
              data-testid={`top-gifters-tab-${t.key}`}
            >
              {t.label}
              <span className="block text-[10px] font-normal text-pink-200/50">
                {t.sub}
              </span>
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <p className="text-pink-200/60 text-sm py-10 text-center" data-testid="top-gifters-loading">
            Loading…
          </p>
        ) : list.length === 0 ? (
          <Card
            className="p-8 text-center bg-white/5 border border-white/10 text-pink-200/70"
            data-testid="top-gifters-empty"
          >
            <p className="text-3xl mb-2">🎁</p>
            <p className="text-sm">No gifts in this window yet — be the first to spark it.</p>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="top-gifters-list">
            {list.map((row) => (
              <Link
                key={row.user_id}
                to={`/artist/${row.user_id}`}
                className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border transition-all hover:scale-[1.01] ${
                  row.rank <= 3
                    ? "bg-gradient-to-r from-amber-500/15 via-yellow-500/10 to-pink-500/10 border-amber-500/40"
                    : "bg-white/5 border-white/10 hover:border-amber-500/30"
                }`}
                data-testid={`top-gifter-row-${row.user_id}`}
              >
                <RankMedal rank={row.rank} />
                <img
                  src={getProfilePhotoUrl(row)}
                  alt={row.artist_name}
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover flex-shrink-0 border-2 border-amber-400/40"
                  onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=120&h=120&fit=crop"; }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold truncate">
                    {row.artist_name}
                  </p>
                  <p className="text-xs sm:text-sm text-amber-200/80">
                    {row.total_coins.toLocaleString()} coins · {row.gift_count} gifts
                  </p>
                  {Array.isArray(row.supporter_badges) && row.supporter_badges.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {row.supporter_badges.map((b) => (
                        <BadgeChip key={b.id} badge={b} />
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}

        <p className="text-[11px] text-pink-200/40 text-center mt-8">
          Weekly &amp; Monthly windows are rolling — they update live with every gift.
        </p>
      </div>
    </div>
  );
};

export default TopGiftersPage;
